import type {NativeSceneOutputReceipt} from '../../../../../../protocol/source-scene-generation';
import { Caption } from '@remotion/captions';
import { DtoResolution } from '@/shared/constants';
import type { CaptionHighlightEffect } from '@/shared/lib/captions/caption-highlight-effect';

export type ContentVariant = {
  type: string; // 'MP4' | 'HLS'
  height: number;
  url: string;
};

export type AssetUploadProgress = {
  progress: number;
  loadedBytes: number;
  totalBytes: number;
};

export type AssetState =
  | {
      type: 'pending-upload';
    }
  | {
      type: 'in-progress';
      progress: AssetUploadProgress;
    }
  | {
      type: 'uploaded';
    }
  | {
      type: 'error';
      error: Error;
      canRetry: boolean;
    };

export type AssetUploadTask = {
  type: 'uploading';
  assetId: string;
  asset: EditorStarterAsset;
  status: AssetUploadProgress;
  startedAt: number;
  id: string;
};

type BaseAsset = {
  filename: string;
  id: string;
  // BE Content.id. 있으면 export 시 BE `/v1.0/export/contents/s3-keys` 로 URL 재조회 대상.
  // GTS_STEP TTS 처럼 Content row 가 없는 임시 합성물은 비워둔다 — 이 경우 remoteUrl 을 그대로 사용한다.
  contentId?: number | null;
  size: number;
  remoteUrl: string | null;
  remoteFileKey: string | null;
  mimeType: string;
  isAsset?: boolean | null;
  extension?: string | null;
  resolution?: DtoResolution | null;
  // 신뢰 마커 — "우리 코드가 생성한 SVG 라 인라인 렌더(dangerouslySetInnerHTML) 해도 안전"임을
  // 표시한다. decorations.ts 같은 내부 SVG 생성기만 true 로 세팅하며, 업로드/BE content 매핑
  // 파이프라인은 절대 세팅하지 않는다. ImageLayer 의 인라인 SVG 경로는 이 플래그로만 게이트되어
  // 신뢰 불가 SVG(임의 업로드 등)가 dangerouslySetInnerHTML 로 들어가는 XSS 를 차단한다.
  trustedInlineSvg?: boolean | null;
  // 인라인 렌더용 SVG 원본 마크업. remoteUrl 이 data URI 가 아니게 된 뒤에도 인라인 경로를
  // 유지하기 위한 통로다 — deck 장식은 에디터 진입 시 S3 로 업로드돼 remoteUrl 이 publicUrl 로
  // 바뀌는데(upload-deck-deco-assets), 그때 <Img> 로 렌더가 갈라지면 같은 아이템이 미리보기와
  // 다르게 그려진다(<img> 는 object-fit 으로만 스케일되어 인라인 <svg> 의 크기 규칙을 재현할 수
  // 없다). trustedInlineSvg 와 함께 있을 때만 쓰이며, 무해성은 렌더 직전 sanitize 가 보장한다.
  inlineSvgMarkup?: string | null;
  // public 폴더 내 정적 파일 경로 (예: 'sound-effects/Beep 01.mp3')
  // 렌더링 시 staticFile()로 변환됨
  staticPath?: string | null;
  // 서버에는 절대경로를 저장하지 않고, desktop/render-service 가 해석 가능한 sourceRef만 저장한다.
  sourceRef?: {
    type: 'DESKTOP_LOCAL' | 'RENDER_SERVICE_NAS';
    rootId: string;
    relativePath: string;
  } | null;
};

export type ImageAsset = BaseAsset & {
  type: 'image';
  width: number;
  height: number;
};

export type VideoAsset = BaseAsset & {
  wakaSceneGeneration?:NativeSceneOutputReceipt;
  /** Legacy import has dimensions but no measured physical duration; whole-video retouch is unavailable. */
  wakaSourceDurationUnknown?:true;
  type: 'video';
  durationInSeconds: number;
  hasAudioTrack: boolean;
  width: number;
  height: number;
  hasAlpha?: boolean;
  /**
   * true면 @remotion/media <Video> 대신 항상 <OffthreadVideo>로 렌더한다.
   * desktop MXF 내보내기의 ProRes 중간본처럼 @remotion/media(mediabunny)가
   * 디코딩하지 못해 OffthreadVideo로 폴백하는 코덱에서, 폴백 sniff 비용을 없애고
   * 처음부터 OffthreadVideo 경로를 타게 한다.
   * Lambda render policy는 율동 CI/Repeat Bridge의 내장 오디오 경계에서 sample이
   * 누락되는 문제를 피할 때도 이 값을 최종 render props에만 주입한다.
   */
  forceOffthreadVideo?: boolean;
  // 다중 해상도 변환 영상 (프리뷰 전용, 렌더링 시 미사용)
  variants?: ContentVariant[] | null;
};

export type GifAsset = BaseAsset & {
  type: 'gif';
  durationInSeconds: number;
  width: number;
  height: number;
};

export type WebpAsset = BaseAsset & {
  type: 'webp';
  durationInSeconds: number;
  width: number;
  height: number;
};

export type AudioAsset = BaseAsset & {
  type: 'audio';
  durationInSeconds: number;
};

// 문서(PDF) 에셋 — pdf/ppt 아이템이 참조한다. pptx 는 업로드 시 PDF 로 변환되어
// 저장되므로 에셋은 항상 PDF 바이트다. 페이지 래스터는 pdf-page-renderer 가 담당.
export type PdfAsset = BaseAsset & {
  type: 'pdf';
  numPages: number;
  /** 1페이지 기준 pt 단위 치수 — 아이템 기본 크기·비율 계산용 */
  pageWidth: number;
  pageHeight: number;
};

/* 해당 Metadata는 리모션 구조와 다른 별개의 데이터로 별도로 관리되며, 리모션 에디터의 정식 구조에서는 사용되지 않는다. */
export type CaptionMetadataType = {
  clipType: 'caption';
  // 서버에서 사용하는 고유 id지만 remotion 자체적으로 id를 사용하기 때문에 네이밍을 clipId로 변경
  clipId: number;
  linkId: number;
  sceneId: number;
  videoStart: number;
  videoEnd: number;
};

export type CaptionAsset = BaseAsset &
  Partial<CaptionMetadataType> & {
    type: 'caption';
    /** Stable source cue identity for frame-cut copies; never used as backend authorization. */
    timelineSourceAssetId?: string;
    /** 지금 화면에 표시 중인 자막. 원문일 수도 번역일 수도 있다. */
    captions: Caption[];
    /**
     * LTS 자막 번역 — 원문↔번역 전환의 SoT.
     * `captions` 가 어느 쪽인지 나타낸다: 'source' | 'en' | 'ko' 등 타겟 언어 코드.
     */
    captionLanguage?: 'source' | string;
    /** 원문 자막(단어 단위). 번역이 붙은 asset 에만 있다. */
    sourceCaptions?: Caption[];
    /** 번역 자막(cue 단위). */
    translatedCaptions?: Caption[];
    /** `translatedCaptions` 의 타겟 언어 코드. */
    translatedLanguage?: string;
    /**
     * 원문으로 되돌릴 때 복원할 item 하이라이트 설정.
     * 번역 자막은 단어 타이밍이 없어 하이라이트를 끄기 때문에 원문 값을 여기 보관한다.
     */
    sourceHighlight?: {
      highlightVisible?: boolean;
      highlightEffect?: CaptionHighlightEffect;
    };
  };

export const isCaptionAsset = (
  asset?: EditorStarterAsset,
): asset is CaptionAsset => {
  if (!asset) return false;
  return asset.type === 'caption';
};

export type EditorStarterAsset =
  | ImageAsset
  | VideoAsset
  | GifAsset
  | WebpAsset
  | AudioAsset
  | PdfAsset
  | CaptionAsset;
